import { WithChildren } from '@/types/common'
import { createContext } from '@/ultils'
import { Breakpoint } from '@mui/material'
import { useCallback, useMemo, useReducer } from 'react'
import { ErrorDetail } from './components/modal/ErrorDetailDiagnostics'

export interface State {
  displayMenu: boolean
  displayMenuRight: boolean
  displaySidebar: boolean
  displayModal: boolean
  sidebarView: SIDEBAR_VIEW
  modalView: MODAL_VIEW
}

const initialState: State = {
  displayMenu: false,
  displayMenuRight: false,
  displaySidebar: false,
  displayModal: false,
  sidebarView: {
    type: '',
    id: '',
  },
  modalView: {
    type: '',
    id: '',
    onSuccess: undefined,
  },
}

export type SIDEBAR_VIEW = {
  type: SIDEBAR_TYPES
  id?: string
  address?: string
  name?: string
  interfaceId?: number
  siteId?: string
  locationId?: number
  viewProgram?: boolean
  interfaceShowing?: number
}

export type MODAL_VIEW = {
  type: MODAL_TYPES
  id?: string
  arrayId?: number[]
  interfaceId?: string
  data?: any
  handleStop?: any
  onSuccess?: (data?: any) => void
  onClose?: () => void
  fullWidth?: boolean
  maxWidth?: Breakpoint
  stationId?: number
  source?: string
  stateStation?: string
  stateProgram?: string
  fertigationData?: number
  errorDiagnostic?: ErrorDetail
  isTestMode?: boolean
  mode?: number
  address?: number
  index?: number
  interfaceShowing?: number
}

type Action =
  | {
      type: 'OPEN_MENU'
    }
  | {
      type: 'CLOSE_MENU'
    }
  | {
      type: 'OPEN_MENU_RIGHT'
    }
  | {
      type: 'CLOSE_MENU_RIGHT'
    }
  | {
      type: 'OPEN_SIDEBAR'
    }
  | {
      type: 'CLOSE_SIDEBAR'
    }
  | {
      type: 'SET_SIDEBAR_VIEW'
      sidebarView: SIDEBAR_VIEW
    }
  | {
      type: 'OPEN_MODAL'
    }
  | {
      type: 'CLOSE_MODAL'
    }
  | {
      type: 'SET_MODAL_VIEW'
      modalView: MODAL_VIEW
    }

export type SIDEBAR_TYPES =
  | 'PROGRAMS_VIEW'
  | 'STATIONS_OVERVIEW'
  | 'STATIONS_LIST_OVERVIEW'
  | 'STATIONS_VIEW'
  | 'EDIT_SOIL_MOISTURE_VIEW'
  | 'PROGRAM_VIEW_DETAIL'
  | 'LEVEL_SENSOR_VIEW'
  | ''
export type MODAL_TYPES =
  | 'OPERATIONS_MODAL'
  | 'CHANGE_PASSWORD_VIEW'
  | 'ADD_STATIONS_GROUP_MODAL'
  | 'MASTER_VIEW'
  | 'DELETE_STATION_VIEW'
  | 'DELETE_PROGRAM_VIEW'
  | 'CONTROLLER_MODAL'
  | 'WEATHER_STATION_MODAL'
  | 'STOP_STATION_MODAL'
  | 'SOIL_MOISTURE_MODAL'
  | 'WATER_CONSUMPTION_HISTORY_MODAL'
  | 'SOIL_MOISTURE_DETAIL'
  | 'OTHER_SENSOR_HISTORY_MODAL'
  | 'OBSERVED_FLOW_MODAL'
  | 'RAIN_OPS_MODAL'
  | 'EMERGENCY_MODAL'
  | 'EXPORT_CSV_MODAL'
  | 'PROGRAM_NOT_FOUND_MODAL'
  | 'NOTIFICATION_MODAL'
  | 'EXCLUSION_MODAL'
  | 'STATION_MODAL'
  | 'PROGRAM_VALIDATION_MODAL'
  | 'ERROR_DIAGNOSTIC_MODAL'
  | 'VERIFICATION_MODAL'
  | 'WATER_TANK_MODAL'
  | 'DELETE_SENSOR_MODAL'
  | 'WATER_SOURCE_MODAL'
  | 'DELETE_WATER_TANK_VIEW'
  | 'WATER_TANK_LEVEL_HISTORY_MODAL'
  | 'WATER_TANK_CHANGE_VALVE_MODAL'
  | 'VERIFICATION_TWOWIRE_MODAL'
  | 'DELETE_ETZ_VIEW'
  | 'DELETE_DYNAMIC_DASHBOARD_VIEW'
  | ''
interface OperationsContextValues extends State {
  // Menu
  openMenu: () => void
  closeMenu: () => void
  toggleMenu: () => void

  // Menu Right
  openMenuRight: () => void
  closeMenuRight: () => void
  toggleMenuRight: () => void

  // Sidebar
  openSidebar: () => void
  closeSidebar: () => void
  setSidebarView: (sidebarView: SIDEBAR_VIEW) => void
  openModal: () => void
  closeModal: () => void
  setModalView: (modalView: MODAL_VIEW) => void
}

function uiReducer(state: State, action: Action) {
  switch (action.type) {
    // Menu
    case 'OPEN_MENU': {
      return {
        ...state,
        displayMenu: true,
      }
    }
    case 'CLOSE_MENU': {
      return {
        ...state,
        displayMenu: false,
      }
    }

    // Menu Right
    case 'OPEN_MENU_RIGHT': {
      return {
        ...state,
        displayMenuRight: true,
      }
    }
    case 'CLOSE_MENU_RIGHT': {
      return {
        ...state,
        displayMenuRight: false,
      }
    }

    // Sidebar
    case 'OPEN_SIDEBAR': {
      return {
        ...state,
        displaySidebar: true,
      }
    }
    case 'CLOSE_SIDEBAR': {
      return {
        ...state,
        displaySidebar: false,
      }
    }
    case 'SET_SIDEBAR_VIEW': {
      return {
        ...state,
        sidebarView: {
          ...action.sidebarView,
        },
      }
    }

    case 'OPEN_MODAL': {
      return {
        ...state,
        displayModal: true,
      }
    }
    case 'CLOSE_MODAL': {
      return {
        ...state,
        displayModal: false,
      }
    }

    case 'SET_MODAL_VIEW': {
      return {
        ...state,
        modalView: action.modalView,
      }
    }
  }
}

const [Provider, useOperationsUI] = createContext<OperationsContextValues>({
  name: 'OperationUI',
})

const OperationsUIProvider = ({ children }: WithChildren) => {
  const [state, dispatch] = useReducer(uiReducer, initialState)

  // Menu

  const openMenu = useCallback(
    () => dispatch({ type: 'OPEN_MENU' }),
    [dispatch],
  )

  const closeMenu = useCallback(
    () => dispatch({ type: 'CLOSE_MENU' }),
    [dispatch],
  )

  const toggleMenu = useCallback(
    () =>
      state.displayMenu
        ? dispatch({ type: 'CLOSE_MENU' })
        : dispatch({ type: 'OPEN_MENU' }),
    [dispatch, state.displayMenu],
  )

  // Menu Right

  const openMenuRight = useCallback(
    () => dispatch({ type: 'OPEN_MENU_RIGHT' }),
    [dispatch],
  )

  const closeMenuRight = useCallback(
    () => dispatch({ type: 'CLOSE_MENU_RIGHT' }),
    [dispatch],
  )

  const toggleMenuRight = useCallback(
    () =>
      state.displayMenu
        ? dispatch({ type: 'CLOSE_MENU_RIGHT' })
        : dispatch({ type: 'OPEN_MENU_RIGHT' }),
    [dispatch, state.displayMenu],
  )

  // Sidebar

  const openSidebar = useCallback(
    () => dispatch({ type: 'OPEN_SIDEBAR' }),
    [dispatch],
  )

  const closeSidebar = useCallback(
    () => dispatch({ type: 'CLOSE_SIDEBAR' }),
    [dispatch],
  )

  const setSidebarView = useCallback(
    (sidebarView: SIDEBAR_VIEW) =>
      dispatch({ type: 'SET_SIDEBAR_VIEW', sidebarView }),
    [dispatch],
  )

  const openModal = useCallback(
    () => dispatch({ type: 'OPEN_MODAL' }),
    [dispatch],
  )

  const closeModal = useCallback(
    () => dispatch({ type: 'CLOSE_MODAL' }),
    [dispatch],
  )

  const setModalView = useCallback(
    (modalView: MODAL_VIEW) => dispatch({ type: 'SET_MODAL_VIEW', modalView }),
    [dispatch],
  )

  const value = useMemo(
    () => ({
      ...state,
      openMenu,
      closeMenu,
      toggleMenu,
      openMenuRight,
      closeMenuRight,
      toggleMenuRight,
      openSidebar,
      closeSidebar,
      setSidebarView,
      openModal,
      closeModal,
      setModalView,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state],
  )

  return <Provider value={value}>{children}</Provider>
}

export { OperationsUIProvider, useOperationsUI }
